import { Phone, Mail, MapPin, Clock, MessageSquare, Navigation } from "lucide-react";
import { useSeo, orgSchema } from "@/hooks/useSeo";
import { PageHero } from "@/components/Layout";
import LeadForm from "@/components/LeadForm";
import { business, serviceAreas } from "@/data/site";
import { track } from "@/lib/analytics";

const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(business.address)}`;


const Contact = () => {
  useSeo({
    title: `Contact & Free Estimate — New York Contractor | ${business.name}`,
    description: "Request a free estimate or contact our New York plumbing, electrical & HVAC team. Fast response, upfront pricing, 24/7 emergency service. Call now.",
    jsonLd: orgSchema,
  });

  return (
    <>
      <PageHero eyebrow="Get in Touch" title="Request a Free Estimate" subtitle="Tell us what you need and we'll respond fast — usually within a few hours during business hours. No obligation, ever." />

      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid lg:grid-cols-5 gap-12" data-testid="contact-section">
        {/* Form */}
        <div className="lg:col-span-3 bg-[#FAFAFA] border-2 border-[#0F2E4C] p-6 sm:p-8">
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight mb-2">Send Us a Message</h2>
          <p className="text-[#475569] mb-6">Fill out this short form and we'll be in touch shortly.</p>
          <LeadForm />
        </div>

        {/* Contact details */}
        <div className="lg:col-span-2 space-y-4">
          <a href={business.phoneHref} onClick={() => track("call_now_click", { location: "contact" })} className="block bg-[#059669] text-white p-6 hover:bg-[#047857] transition-colors" data-testid="contact-call-card">
            <div className="flex items-center gap-3 mb-1"><Phone className="w-6 h-6" /> <span className="text-sm uppercase tracking-wide font-semibold">Call Now</span></div>
            <div className="font-display font-black text-2xl">{business.phoneDisplay}</div>
          </a>

          <div className="bg-white border border-[#E2E8F0] p-6 space-y-5">
            <a href={business.emailHref} onClick={() => track("email_click", { location: "contact" })} className="flex items-start gap-3 group" data-testid="contact-email">
              <Mail className="w-5 h-5 text-[#059669] mt-0.5" />
              <div><div className="text-sm uppercase tracking-wide font-semibold text-[#475569]">Email</div><div className="font-medium group-hover:text-[#059669] break-all">{business.email}</div></div>
            </a>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#059669] mt-0.5" />
              <div className="flex-1"><div className="text-sm uppercase tracking-wide font-semibold text-[#475569]">Address</div><div className="font-medium">{business.address}</div>
                <a href={directionsUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("directions_click", { location: "contact" })} className="inline-flex items-center gap-1.5 mt-2 text-sm font-bold text-[#059669] hover:underline" data-testid="contact-directions-btn"><Navigation className="w-4 h-4" /> Get Directions</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#059669] mt-0.5" />
              <div>
                <div className="text-sm uppercase tracking-wide font-semibold text-[#475569] mb-1">Hours</div>
                {business.hours.map((h) => <div key={h.day} className="font-medium text-sm">{h.day}: {h.time}</div>)}
              </div>
            </div>
          </div>

          {/* Map placeholder */}
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="contact-map">
            <div className="flex items-center gap-2 mb-3 text-[#475569]"><MapPin className="w-5 h-5 text-[#059669]" /> <span className="text-sm uppercase tracking-wide font-semibold">Service Map</span></div>
            <div className="aspect-video bg-[#0F2E4C] flex flex-col items-center justify-center text-center text-white p-4">
              <MessageSquare className="w-8 h-8 text-[#34D399] mb-2" />
              <p className="text-sm text-slate-300">Google Maps / Business Profile embed goes here.</p>
              <p className="text-xs text-slate-500 mt-1">Add the client's real map embed & GBP link before launch.</p>
            </div>
            <p className="text-sm text-[#475569] mt-3">Serving {serviceAreas.slice(0, 5).join(", ")} & all of New York.</p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
